import { useState } from "react";
import { Plane, Building2, Users2, TrendingUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import EnquiryDialog from "@/components/EnquiryDialog";
import SectionHeading from "@/components/SectionHeading";

const BENEFITS = [
  { icon: TrendingUp, title: "Commercial Ride Opportunities", desc: "Guidance to connect with airport, corporate and travel operators." },
  { icon: Users2, title: "Onboarding Guidance", desc: "Documentation and process support during operator onboarding." },
  { icon: Building2, title: "Setup Support", desc: "Advice on driver requirements, uniform, GPS and vehicle readiness." },
  { icon: CheckCircle2, title: "Ongoing Assistance", desc: "Support during initial operations and renewal cycles." },
];

export default function AirportAttachment() {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="airport-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.pexels.com/photos/15549900/pexels-photo-15549900.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050B24]/50 via-[#050B24]/80 to-[#050B24]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
            <Plane className="w-4 h-4" /> Airport Attachment
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Get Your T-Permit Vehicle <span className="text-gradient-red">On The Runway</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl leading-relaxed">
            We assist customers in understanding available airport and commercial fleet
            attachment options where eligible. Availability depends on operator
            requirements, documentation and market conditions.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              onClick={() => setOpen(true)}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 h-13"
              data-testid="airport-enquire-btn"
            >
              Enquire About Attachment
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="Benefits" title="What our support may include" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="glass glass-hover rounded-2xl p-6" data-testid={`airport-benefit-${i}`}>
                <b.icon className="w-6 h-6 text-red-500 mb-4" />
                <div className="font-display font-bold text-white text-lg">{b.title}</div>
                <div className="text-sm text-slate-400 mt-2 leading-relaxed">{b.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 glass rounded-3xl p-8 border-l-4 border-red-500">
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              <span className="text-white font-semibold">Important:</span> Airport
              attachment eligibility, timelines and terms are determined by the
              respective airport / fleet operator. Be Easy Cars provides guidance
              and coordination — final onboarding is subject to operator approval
              and current market conditions.
            </p>
          </div>
        </div>
      </section>

      <EnquiryDialog open={open} onOpenChange={setOpen} defaultVehicle="" />
    </div>
  );
}
